import React, { useState } from 'react';
import styled from 'styled-components';
import { MapPin, Phone, Mail, Clock, Award, Users, TrendingUp, Shield } from 'lucide-react';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 120px 0 80px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('/images/pattern.svg') repeat;
    opacity: 0.1;
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const HeroTitle = styled.h1`
  font-size: 48px;
  font-weight: 800;
  margin-bottom: 20px;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 20px;
  margin-bottom: 30px;
  opacity: 0.9;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ContentWrapper = styled.div`
  background: white;
  margin-top: -40px;
  border-radius: 20px 20px 0 0;
  position: relative;
  z-index: 2;
`;

const Section = styled.section`
  padding: 80px 0;
  
  &:nth-child(even) {
    background: #f8fafc;
  }
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.h2`
  font-size: 32px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 50px;
  color: #1f2937;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 40px;
`;

const Card = styled.div`
  background: white;
  padding: 40px 30px;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  transition: transform 0.3s, box-shadow 0.3s;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
  }
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 25px;
  color: white;
`;

const CardTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #1f2937;
`;

const CardDescription = styled.p`
  color: #6b7280;
  line-height: 1.6;
  font-size: 14px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 30px;
  margin: 50px 0;
`;

const StatCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
`;

const StatNumber = styled.div`
  font-size: 36px;
  font-weight: 800;
  color: #667eea;
  margin-bottom: 10px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
`;

const ContactCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 20px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
`;

const ContactIcon = styled.div`
  width: 50px;
  height: 50px;
  background: #f3f4f6;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #667eea;
  flex-shrink: 0;
`;

const ContactInfo = styled.div`
  flex: 1;
`;

const ContactLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 5px;
`;

const ContactValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
`;

const TimelineContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const TimelineItem = styled.div`
  display: flex;
  margin-bottom: 30px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 25px;
    top: 50px;
    bottom: -30px;
    width: 2px;
    background: #e5e7eb;
  }
  
  &:last-child::before {
    display: none;
  }
`;

const TimelineIcon = styled.div`
  width: 50px;
  height: 50px;
  background: #667eea;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  z-index: 1;
`;

const TimelineContent = styled.div`
  margin-left: 20px;
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  flex: 1;
`;

const TimelineYear = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #667eea;
  margin-bottom: 8px;
`;

const TimelineTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
`;

const TimelineDescription = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
`;

function CompanyInfo() {
  const values = [
    {
      icon: <Award size={32} />,
      title: "품질 우선",
      description: "고객에게 최고 품질의 상품만을 제공하기 위해 엄격한 품질관리 시스템을 운영합니다."
    },
    {
      icon: <Users size={32} />,
      title: "고객 중심",
      description: "고객의 만족이 우리의 최우선 가치입니다. 고객의 소리에 귀 기울이고 최선을 다합니다."
    },
    {
      icon: <TrendingUp size={32} />,
      title: "지속적 성장",
      description: "끊임없는 혁신과 개선을 통해 더 나은 서비스를 제공하며 함께 성장해갑니다."
    },
    {
      icon: <Shield size={32} />,
      title: "신뢰와 안전",
      description: "안전한 거래와 개인정보 보호를 위해 최신 보안 시스템을 구축하고 있습니다."
    }
  ];

  const stats = [
    { number: "10,000+", label: "누적 회원수" },
    { number: "50,000+", label: "판매 상품수" },
    { number: "99.8%", label: "고객 만족도" },
    { number: "24/7", label: "고객지원 서비스" }
  ];

  const timeline = [
    {
      year: "2024",
      title: "AntsMarket 런칭",
      description: "혁신적인 온라인 쇼핑 플랫폼 AntsMarket을 정식 오픈하여 고객들에게 새로운 쇼핑 경험을 제공했습니다."
    },
    {
      year: "2023",
      title: "사업 확장",
      description: "다양한 카테고리의 상품을 확장하고 파트너십을 통해 더 많은 브랜드와 협력하게 되었습니다."
    },
    {
      year: "2022",
      title: "시스템 고도화",
      description: "고객 편의를 위한 AI 추천 시스템과 개인화 서비스를 도입하여 쇼핑 경험을 개선했습니다."
    },
    {
      year: "2021",
      title: "회사 설립",
      description: "더 나은 온라인 쇼핑 경험을 제공하겠다는 비전으로 AntsMarket을 설립했습니다."
    }
  ];

  return (
    <Container>
      <HeroSection>
        <HeroContent>
          <HeroTitle>AntsMarket</HeroTitle>
          <HeroSubtitle>
            고객 중심의 혁신적인 쇼핑 경험을 제공하는<br />
            믿을 수 있는 온라인 쇼핑 파트너입니다
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      <ContentWrapper>
        <Section>
          <ContentContainer>
            <SectionTitle>우리의 가치</SectionTitle>
            <Grid>
              {values.map((value, index) => (
                <Card key={index}>
                  <IconWrapper>{value.icon}</IconWrapper>
                  <CardTitle>{value.title}</CardTitle>
                  <CardDescription>{value.description}</CardDescription>
                </Card>
              ))}
            </Grid>
          </ContentContainer>
        </Section>

        <Section>
          <ContentContainer>
            <SectionTitle>AntsMarket의 성과</SectionTitle>
            <StatsGrid>
              {stats.map((stat, index) => (
                <StatCard key={index}>
                  <StatNumber>{stat.number}</StatNumber>
                  <StatLabel>{stat.label}</StatLabel>
                </StatCard>
              ))}
            </StatsGrid>
          </ContentContainer>
        </Section>

        <Section>
          <ContentContainer>
            <SectionTitle>우리의 여정</SectionTitle>
            <TimelineContainer>
              {timeline.map((item, index) => (
                <TimelineItem key={index}>
                  <TimelineIcon>
                    <Award size={24} />
                  </TimelineIcon>
                  <TimelineContent>
                    <TimelineYear>{item.year}</TimelineYear>
                    <TimelineTitle>{item.title}</TimelineTitle>
                    <TimelineDescription>{item.description}</TimelineDescription>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </TimelineContainer>
          </ContentContainer>
        </Section>

        <Section>
          <ContentContainer>
            <SectionTitle>연락처 정보</SectionTitle>
            <ContactGrid>
              <ContactCard>
                <ContactIcon>
                  <MapPin size={24} />
                </ContactIcon>
                <ContactInfo>
                  <ContactLabel>주소</ContactLabel>
                  <ContactValue>
                    서울시 강남구 테헤란로 123<br />
                    AntsMarket 본사 10층
                  </ContactValue>
                </ContactInfo>
              </ContactCard>

              <ContactCard>
                <ContactIcon>
                  <Phone size={24} />
                </ContactIcon>
                <ContactInfo>
                  <ContactLabel>전화번호</ContactLabel>
                  <ContactValue>02-1234-5678</ContactValue>
                </ContactInfo>
              </ContactCard>

              <ContactCard>
                <ContactIcon>
                  <Mail size={24} />
                </ContactIcon>
                <ContactInfo>
                  <ContactLabel>이메일</ContactLabel>
                  <ContactValue>contact@antsmarket.com</ContactValue>
                </ContactInfo>
              </ContactCard>

              <ContactCard>
                <ContactIcon>
                  <Clock size={24} />
                </ContactIcon>
                <ContactInfo>
                  <ContactLabel>운영시간</ContactLabel>
                  <ContactValue>
                    평일 09:00 - 18:00<br />
                    주말/공휴일 휴무
                  </ContactValue>
                </ContactInfo>
              </ContactCard>
            </ContactGrid>
          </ContentContainer>
        </Section>
      </ContentWrapper>
    </Container>
  );
}

export default CompanyInfo;